// external
import React, { FC, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

// internal
import { Team } from '../../../../models/training.models';
import COEYearDateRangeComponent from '../../../../components/coe-date-range/coe-year-date-range.component';
import RaceEventCard from '../../../../components/coe-race-log/race-event-card';
import CreateTeamRaceLogsModal from '../../../../components/coe-race-log/create-team-race-logs-modal';
import type {
  Race,
  RaceLogEntry,
  CreateTeamRaceLogsPayload,
  UpdateRaceLogPayload,
  TeamRacesSummary,
  TeamRacesResponse,
} from '../../../../models/race.models';
import RaceLogsService from '../../../../services/race-logs.service';
import AnalyticsService from '../../../../services/analytics.service';
import useService from '../../../../hooks/service.hook';
import useSnackbarHelper from '../../../../hooks/snackbar.hook';
import { palette } from '../../../../theme';

interface RaceLogPageProps {
  team: Team;
  refreshTeam: () => void;
}

const EMPTY_SUMMARY: TeamRacesSummary = {
  total_races: 0,
  podiums: 0,
  wins: 0,
};

interface SeasonStatCardProps {
  label: string;
  value: number;
  accent: 'primary' | 'warning' | 'success';
}

const SeasonStatCard: FC<SeasonStatCardProps> = ({ label, value, accent }) => {
  const colorMap = {
    primary: { main: palette.primary.main, bgFrom: palette.primary.light, bgTo: alpha(palette.primary.main, 0.18) },
    warning: { main: palette.warning.dark, bgFrom: palette.warning.light, bgTo: alpha(palette.warning.main, 0.25) },
    success: { main: palette.success.dark, bgFrom: palette.success.light, bgTo: alpha(palette.success.main, 0.22) },
  };
  const colors = colorMap[accent];

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 2,
        borderRadius: '16px',
        background: `linear-gradient(135deg, ${colors.bgFrom} 0%, ${colors.bgTo} 100%)`,
        border: `1px solid ${alpha(colors.main, 0.12)}`,
      }}
    >
      <Typography variant="body2" sx={{ color: palette.text.secondary, fontWeight: 500, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ color: colors.main, fontWeight: 700, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
};

function parseTeamRacesPayload(data: unknown): { races: Race[]; summary: TeamRacesSummary } {
  if (Array.isArray(data)) {
    return { races: data as Race[], summary: { ...EMPTY_SUMMARY, total_races: data.length } };
  }
  if (data && typeof data === 'object') {
    const payload = data as Partial<TeamRacesResponse>;
    const races = Array.isArray(payload.races) ? payload.races : [];
    const summary: TeamRacesSummary = {
      total_races: payload.summary?.total_races ?? races.length,
      podiums: payload.summary?.podiums ?? 0,
      wins: payload.summary?.wins ?? 0,
    };
    return { races, summary };
  }
  return { races: [], summary: EMPTY_SUMMARY };
}

const RaceLogPage: FC<RaceLogPageProps> = ({ team }) => {
  const navigate = useNavigate();
  const [expandedRaceId, setExpandedRaceId] = useState<number | null>(null);
  const [races, setRaces] = useState<Race[]>([]);
  const [summary, setSummary] = useState<TeamRacesSummary>(EMPTY_SUMMARY);
  const [racesLoading, setRacesLoading] = useState(true);
  const [racesError, setRacesError] = useState<string | null>(null);
  const [raceLogsByEventId, setRaceLogsByEventId] = useState<Record<number, RaceLogEntry[]>>({});
  const [loadingEntriesForRaceId, setLoadingEntriesForRaceId] = useState<number | null>(null);
  const [annualStartDate, setAnnualStartDate] = useState<dayjs.Dayjs | null>(null);
  const [selectedPeriodStart, setSelectedPeriodStart] = useState<dayjs.Dayjs | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sportFilter, setSportFilter] = useState('');
  const [sportTypeFilter, setSportTypeFilter] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [addRaceModalOpen, setAddRaceModalOpen] = useState(false);
  const [selectedAddRace, setSelectedAddRace] = useState<Race | null>(null);
  const fetchedRaceIdsRef = useRef<Set<number>>(new Set());

  const dateRange = useMemo(() => {
    if (!selectedPeriodStart) return null;
    const periodEnd = selectedPeriodStart.add(1, 'year').subtract(1, 'day');
    return {
      start_date: selectedPeriodStart.startOf('day').toISOString(),
      end_date: periodEnd.endOf('day').toISOString(),
    };
  }, [selectedPeriodStart]);

  const raceLogsService = useService(RaceLogsService);
  const analyticsService = useService(AnalyticsService);
  const { showSnackbar } = useSnackbarHelper();

  const teamId = team?.id ?? team?.teamId ?? '';
  const isCoach = team?.role?.toLowerCase() === 'coach';

  const sportOptions = useMemo(() => {
    const set = new Set<string>();
    races.forEach((r) => {
      if (r.sport) set.add(r.sport);
    });
    return Array.from(set).sort();
  }, [races]);

  const sportTypeOptions = useMemo(() => {
    const set = new Set<string>();
    races.forEach((r) => {
      if (r.sport_type) set.add(r.sport_type);
    });
    return Array.from(set).sort();
  }, [races]);

  const filteredRaces = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return races.filter((race) => {
      if (sportFilter && race.sport !== sportFilter) return false;
      if (sportTypeFilter && race.sport_type !== sportTypeFilter) return false;
      if (!q) return true;
      const haystack = `${race.name} ${race.location} ${race.sport} ${race.sport_type}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [races, searchQuery, sportFilter, sportTypeFilter]);

  const activeFilterCount = (sportFilter ? 1 : 0) + (sportTypeFilter ? 1 : 0);

  useEffect(() => {
    const applyCurrentPeriod = (currentPeriodStart: dayjs.Dayjs) => {
      setAnnualStartDate(currentPeriodStart);
      setSelectedPeriodStart(currentPeriodStart);
    };
    analyticsService
      .getAnalyticsAnnualStartDate()
      .then((res) => {
        const annualStartDateStr = res.data.data.annual_start_date;
        let currentPeriodStart: dayjs.Dayjs;
        if (annualStartDateStr) {
          const storedDate = dayjs(annualStartDateStr);
          const currentYear = dayjs().year();
          currentPeriodStart = storedDate.year(currentYear);
          if (dayjs().isBefore(currentPeriodStart)) {
            currentPeriodStart = currentPeriodStart.subtract(1, 'year');
          }
        } else {
          currentPeriodStart = dayjs().startOf('year');
        }
        applyCurrentPeriod(currentPeriodStart);
      })
      .catch(() => {
        applyCurrentPeriod(dayjs().startOf('year'));
      });
  }, [analyticsService]);

  const handleDateChange = useCallback(
    (newDate: dayjs.Dayjs) => {
      if (annualStartDate) {
        setSelectedPeriodStart(
          dayjs().year(newDate.year()).month(annualStartDate.month()).date(annualStartDate.date())
        );
      } else {
        setSelectedPeriodStart(newDate);
      }
    },
    [annualStartDate]
  );

  useEffect(() => {
    if (!selectedPeriodStart) return;
    setRaceLogsByEventId({});
    fetchedRaceIdsRef.current.clear();
    setExpandedRaceId(null);
  }, [selectedPeriodStart]);

  useEffect(() => {
    if (!teamId || !dateRange) {
      if (!teamId) {
        setRaces([]);
        setSummary(EMPTY_SUMMARY);
        setRacesLoading(false);
      }
      return;
    }
    let cancelled = false;
    setRacesLoading(true);
    setRacesError(null);
    setRaces([]);
    raceLogsService
      .getTeamRaces(teamId, dateRange)
      .then((res) => {
        if (cancelled) return;
        const { races: list, summary: nextSummary } = parseTeamRacesPayload(res.data.data);
        const sorted = [...list].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        setRaces(sorted);
        setSummary(nextSummary);
      })
      .catch((error) => {
        if (cancelled) return;
        const message =
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Failed to load team races.';
        setRacesError(message);
        setRaces([]);
        setSummary(EMPTY_SUMMARY);
      })
      .finally(() => {
        if (!cancelled) setRacesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [teamId, dateRange, raceLogsService]);

  useEffect(() => {
    if (!teamId || !dateRange || racesLoading || races.length === 0) return;

    let cancelled = false;
    const racesToFetch = races.filter((race) => !fetchedRaceIdsRef.current.has(race.id));
    if (racesToFetch.length === 0) return;

    racesToFetch.forEach((race) => {
      fetchedRaceIdsRef.current.add(race.id);
    });

    Promise.all(
      racesToFetch.map((race) =>
        raceLogsService
          .getTeamRaceLogs(teamId, String(race.id), dateRange)
          .then((res) => {
            const data = res.data.data;
            const list = Array.isArray(data) ? (data as RaceLogEntry[]) : [];
            return { raceId: race.id, list };
          })
          .catch(() => ({ raceId: race.id, list: [] as RaceLogEntry[] }))
      )
    ).then((results) => {
      if (cancelled) return;
      setRaceLogsByEventId((prev) => {
        const next = { ...prev };
        results.forEach(({ raceId, list }) => {
          next[raceId] = list;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [teamId, dateRange, races, racesLoading, raceLogsService]);

  useEffect(() => {
    if (!teamId || !dateRange || expandedRaceId == null) return;
    if (fetchedRaceIdsRef.current.has(expandedRaceId)) return;

    fetchedRaceIdsRef.current.add(expandedRaceId);
    setLoadingEntriesForRaceId(expandedRaceId);
    raceLogsService
      .getTeamRaceLogs(teamId, String(expandedRaceId), dateRange)
      .then((res) => {
        const data = res.data.data;
        const list = Array.isArray(data) ? (data as RaceLogEntry[]) : [];
        setRaceLogsByEventId((prev) => ({ ...prev, [expandedRaceId]: list }));
      })
      .catch(() => {
        setRaceLogsByEventId((prev) => ({ ...prev, [expandedRaceId]: [] }));
      })
      .finally(() => {
        setLoadingEntriesForRaceId(null);
      });
  }, [teamId, expandedRaceId, raceLogsService, dateRange]);

  const handleAddTeamRaceLog = useCallback(
    async (race: Race, payload: CreateTeamRaceLogsPayload) => {
      if (!teamId) return;
      await raceLogsService.createTeamRaceLogs(teamId, String(race.id), payload);
      const res = await raceLogsService.getTeamRaceLogs(teamId, String(race.id), dateRange);
      const data = res.data.data;
      const list = Array.isArray(data) ? (data as RaceLogEntry[]) : [];
      setRaceLogsByEventId((prev) => ({ ...prev, [race.id]: list }));
      fetchedRaceIdsRef.current.add(race.id);
    },
    [teamId, raceLogsService, dateRange]
  );

  const handleEditEntry = useCallback(
    async (raceId: number, entryId: number, payload: UpdateRaceLogPayload) => {
      if (!teamId) return;
      try {
        await raceLogsService.updateRaceLog(entryId, payload);
        const res = await raceLogsService.getTeamRaceLogs(teamId, String(raceId), dateRange);
        const data = res.data.data;
        const list = Array.isArray(data) ? (data as RaceLogEntry[]) : [];
        setRaceLogsByEventId((prev) => ({ ...prev, [raceId]: list }));
      } catch (error) {
        const message =
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Failed to update race log.';
        showSnackbar(message, 'error');
      }
    },
    [teamId, raceLogsService, showSnackbar, dateRange]
  );

  const handleDeleteEntry = useCallback(
    async (raceId: number, entryId: number) => {
      if (!teamId) return;
      try {
        await raceLogsService.deleteRaceLog(entryId);
        const res = await raceLogsService.getTeamRaceLogs(teamId, String(raceId), dateRange);
        const data = res.data.data;
        const list = Array.isArray(data) ? (data as RaceLogEntry[]) : [];
        setRaceLogsByEventId((prev) => ({ ...prev, [raceId]: list }));
      } catch (error) {
        const message =
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Failed to delete race log.';
        showSnackbar(message, 'error');
      }
    },
    [teamId, raceLogsService, showSnackbar, dateRange]
  );

  const handleOpenTrainingFromRaceEvent = useCallback(
    (trainingId: number, viewerType: 'athlete' | 'coach' | 'guardian') => {
      navigate(`/home?trainingId=${trainingId}&trainingType=${viewerType}`);
    },
    [navigate]
  );

  const handleOpenAddRace = () => {
    setSelectedAddRace(races[0] ?? null);
    setAddRaceModalOpen(true);
  };

  const handleClearFilters = () => {
    setSportFilter('');
    setSportTypeFilter('');
  };

  if (!selectedPeriodStart) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        px: 2,
        pb: 3,
        gap: 2,
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          py: 1.5,
          backgroundColor: palette.background.default,
          borderBottom: `1px solid ${palette.divider}`,
        }}
      >
        <COEYearDateRangeComponent value={selectedPeriodStart} setValue={handleDateChange} />

        <TextField
          size="small"
          placeholder="Search races..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: palette.text.secondary }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 200,
            flex: '1 1 200px',
            maxWidth: 320,
            '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: palette.background.paper },
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={(e) => setFilterAnchorEl(e.currentTarget)}
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              borderColor: palette.divider,
              color: palette.text.primary,
            }}
          >
            Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </Button>
          {isCoach && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddRace}
              disabled={races.length === 0}
              sx={{ textTransform: 'none', borderRadius: '10px' }}
            >
              Add Race
            </Button>
          )}
        </Box>
      </Box>

      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 240 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="race-sport-filter-label">Sport</InputLabel>
            <Select
              labelId="race-sport-filter-label"
              label="Sport"
              value={sportFilter}
              onChange={(e) => setSportFilter(e.target.value)}
            >
              <MenuItem value="">All sports</MenuItem>
              {sportOptions.map((sport) => (
                <MenuItem key={sport} value={sport}>
                  {sport}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel id="race-sport-type-filter-label">Sport type</InputLabel>
            <Select
              labelId="race-sport-type-filter-label"
              label="Sport type"
              value={sportTypeFilter}
              onChange={(e) => setSportTypeFilter(e.target.value)}
            >
              <MenuItem value="">All types</MenuItem>
              {sportTypeOptions.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" onClick={handleClearFilters} disabled={activeFilterCount === 0}>
            Clear filters
          </Button>
        </Box>
      </Popover>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.75 }}>
        <SeasonStatCard label="Total Races" value={summary.total_races} accent="primary" />
        <SeasonStatCard label="Podiums" value={summary.podiums} accent="warning" />
        <SeasonStatCard label="Wins" value={summary.wins} accent="success" />
      </Box>

      {racesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : racesError ? (
        <Box sx={{ py: 4, px: 2 }}>
          <Typography variant="body1" sx={{ color: palette.text.secondary }}>
            {racesError}
          </Typography>
        </Box>
      ) : filteredRaces.length === 0 ? (
        <Box sx={{ py: 4, px: 2 }}>
          <Typography variant="body1" sx={{ color: palette.text.secondary }}>
            {races.length === 0 ? 'No races in this period.' : 'No races match your search or filters.'}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            width: '100%',
          }}
        >
          {filteredRaces.map((race) => (
            <RaceEventCard
              key={race.id}
              race={race}
              expanded={expandedRaceId === race.id}
              onToggle={() => setExpandedRaceId((prev) => (prev === race.id ? null : race.id))}
              entries={raceLogsByEventId[race.id] ?? []}
              entriesLoading={loadingEntriesForRaceId === race.id}
              onEntriesChange={(updated) => setRaceLogsByEventId((prev) => ({ ...prev, [race.id]: updated }))}
              teamId={teamId || undefined}
              onAddTeamRaceLog={handleAddTeamRaceLog}
              onEditEntry={(entryId, payload) => handleEditEntry(race.id, entryId, payload)}
              onDeleteEntry={(entryId) => handleDeleteEntry(race.id, entryId)}
              onOpenTraining={handleOpenTrainingFromRaceEvent}
              isCoach={isCoach}
              isGuardian={team?.role?.toLowerCase() === 'guardian'}
            />
          ))}
        </Box>
      )}

      {isCoach && teamId && (
        <CreateTeamRaceLogsModal
          open={addRaceModalOpen}
          onClose={() => {
            setAddRaceModalOpen(false);
            setSelectedAddRace(null);
          }}
          race={selectedAddRace}
          races={races}
          onRaceChange={setSelectedAddRace}
          existingEntries={selectedAddRace ? raceLogsByEventId[selectedAddRace.id] ?? [] : []}
          teamId={teamId}
          onSubmit={async (payload) => {
            if (!selectedAddRace) return;
            await handleAddTeamRaceLog(selectedAddRace, payload);
          }}
        />
      )}
    </Box>
  );
};

export default RaceLogPage;
